"use client";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, Mail, FolderOpen, Users, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import nextcloudLogo from "@/assets/nextcloud-logo.png";
import zimbraLogo from "@/assets/zimbra-logo.webp";
import keycloakLogo from "@/assets/keycloak-logo.png";
import lasuiteLogo from "@/assets/lasuite-logo.png";

const RestorationSection = () => {
  const { t } = useTranslation();

  const dataTypes = [
    { icon: Users, labelKey: "restoration.directory" },
    { icon: Mail, labelKey: "restoration.emails" },
    { icon: Calendar, labelKey: "restoration.calendars" },
    { icon: FolderOpen, labelKey: "restoration.files" },
  ];

  const services = [
    { logo: lasuiteLogo, name: "La Suite", alt: "La Suite" },
    { logo: keycloakLogo, name: "Keycloak", alt: "Keycloak" },
    { logo: nextcloudLogo, name: "Nextcloud", alt: "Nextcloud" },
    { logo: zimbraLogo, name: "Zimbra", alt: "Zimbra" },
    { logo: null, name: t("restoration.andMore"), alt: "And more" },
  ];

  return (
    <section className="py-20 md:py-28 bg-secondary">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-3">
            {t("restoration.title")}
          </h2>
          <p className="text-foreground text-lg font-medium max-w-2xl mx-auto mb-3">
            {t("restoration.outcome")}
          </p>
          <p className="text-muted-foreground text-sm max-w-2xl mx-auto mb-2">{t("restoration.scenario")}</p>
          <p className="text-muted-foreground text-sm max-w-2xl mx-auto">{t("restoration.transition")}</p>
        </motion.div>

        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-4">
          {/* Left: Data types */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex-1 space-y-4"
          >
            {dataTypes.map((item, i) => (
              <motion.div
                key={item.labelKey}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-center gap-3 rounded-lg border border-border bg-background p-4 shadow-sm"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <span className="font-medium text-foreground text-sm">{t(item.labelKey)}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* Center: Arrows */}
          <div className="flex flex-col items-center gap-3 py-4 md:py-0 md:px-4">
            {services.map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.3 + i * 0.1 }}
                className="flex items-center"
              >
                <div className="h-px w-8 md:w-16 bg-primary/30" />
                <ArrowRight className="h-5 w-5 text-primary" />
              </motion.div>
            ))}
          </div>

          {/* Right: Service logos */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-1 space-y-4"
          >
            {services.map((service, i) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                className="flex items-center gap-3 rounded-lg border border-border bg-background p-4 shadow-sm"
              >
                {service.logo ? (
                  <img src={String(service.logo)} alt={service.alt} className="h-10 w-10 object-contain" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                    <span className="text-primary font-bold text-lg">+</span>
                  </div>
                )}
                <span className="font-medium text-foreground text-sm">{service.name}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-14"
        >
          <Button variant="outline" size="lg" className="gap-2 text-sm font-semibold" asChild>
            <Link to="/sovereign-continuity">
              {t("restoration.learnContinuity")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div> */}
      </div>
    </section>
  );
};

export default RestorationSection;
